import { useContext, useEffect } from "react";
import { Box, Collapse, ToggleButton, ToggleButtonGroup } from "@mui/material";
import * as geoprocessor from "@arcgis/core/rest/geoprocessor";
import JobInfo from "@arcgis/core/rest/support/JobInfo";
import CityContext from "../../contexts/CityContext";
import { MapViewContext } from "../../contexts/MapViewContext";
import MapView from "@arcgis/core/views/MapView";
import Map from "@arcgis/core/Map";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import Color from "@arcgis/core/Color";
import SimpleLineSymbol from "@arcgis/core/symbols/SimpleLineSymbol";
import Graphic from "@arcgis/core/Graphic";
import { CityProfileVisibleContext } from "../../contexts/CityProfileVisibleContext";
import { AlignmentContext } from "../../contexts/AlignmentContext";
import EvaluatorContext from "../../contexts/EvaluatorContext";
import ParkContext from "../../contexts/ParkContext";
import CityInfoForPanel from "./CityInfoForPanel";
import {
	parkServePlaceLayerId,
	parkServeUrbanLayerId,
	priorityAreaUrbanFLId,
	priorityAreaPlaceFLId,
    dataFields,
} from "../../constants";
import UrbanAreaInfoForPanel from "./UrbanAreaInfoForPanel";
import NationalStats from "./NationalStats";
import FeatureFilter from "@arcgis/core/layers/support/FeatureFilter";
import ClickedParkPanel from "./ClickedParkPanel";
import EvaluatorResultsPanel from "./EvaluatorResultsPanel";
import useMediaQuery from "@mui/material/useMediaQuery";
import { trackEvent } from "../../hooks/useAnalytics";

export default function RightPanel() {
	const { city, urban, setUrban } = useContext(CityContext);
	const { gpArray, gpJson } = useContext(EvaluatorContext);
	const {
		clickedPark,
		setClickedPark,
		clickedParkServiceArea,
		setClickedParkServiceArea,
	} = useContext(ParkContext);
	const { visible, setVis } = useContext(CityProfileVisibleContext);
	const mapView = useContext(MapViewContext);
	const { alignment, setAlignment } = useContext(AlignmentContext);
	const matches = useMediaQuery("(max-width:1000px)");
	let parkServeUrbanLayer: __esri.FeatureLayer;
	let parkServePlaceLayer: __esri.FeatureLayer;
	let priorityAreaUrbanFL: __esri.FeatureLayer;
	let priorityAreaPlaceFL: __esri.FeatureLayer;

	const miniMap = new Map();

	const miniMapView = new MapView({
		map: miniMap,
		container: "minimap",
		background: {
			color: new Color([242, 242, 242]),
		},
		spatialReference: {
			wkid: 102100,
		},
	});

	const gl = new GraphicsLayer();
	miniMap.add(gl);

	// @ts-ignore
	const polygonSymbol: SimpleFillSymbol = {
		type: "simple-fill",
		color: new Color([250, 250, 250]),
		outline: new SimpleLineSymbol({
			style: "solid",
			color: new Color([0, 153, 51]),
			width: 1,
		}),
	};

	miniMapView.ui.components = [];

	useEffect(() => {
		if (alignment == "park") {
			miniMapView.when(() => {
				if (clickedPark?.geometry) {
					gl.removeAll();
					const cityGraphic = new Graphic({
						geometry: clickedPark?.geometry,
						symbol: polygonSymbol,
					});
					gl.add(cityGraphic);
					miniMapView.goTo({
						target: clickedPark?.geometry.extent.expand(1.3),
					});
				}
				disableZooming();
			});
		}
	}, [city, alignment, miniMapView]);

	useEffect(() => {
		if (mapView) {
			//@ts-ignore
			parkServePlaceLayer = mapView.map.findLayerById(parkServePlaceLayerId);
			//@ts-ignore
			parkServeUrbanLayer = mapView.map.findLayerById(parkServeUrbanLayerId);
			//@ts-ignore
			priorityAreaUrbanFL = mapView.map.findLayerById(priorityAreaUrbanFLId);
			//@ts-ignore
			priorityAreaPlaceFL = mapView.map.findLayerById(priorityAreaPlaceFLId);
			//trackRightPanelTabs(alignment);
			trackEvent("right_panel", alignment);
			if (alignment == "urban" && urban) {
				if (priorityAreaPlaceFL.visible) {
					priorityAreaUrbanFL.visible = true;
				}
				parkServePlaceLayer.visible = false;
				priorityAreaPlaceFL.visible = false;
				parkServeUrbanLayer.visible = true;
				mapView.goTo(urban.geometry);
				mapView.whenLayerView(priorityAreaUrbanFL).then((layerView) => {
					layerView.filter = new FeatureFilter({
						where: `${dataFields["UA_ID"]} = '${urban.attributes[dataFields["UA_ID"]]}' AND ${dataFields["Outside10MWOnly"]} = 1`,
					});
				});
				mapView.whenLayerView(parkServeUrbanLayer).then((layerView) => {
					layerView.filter = new FeatureFilter({
						where: `${dataFields["UA_ID"]} = '${urban.attributes[dataFields["UA_ID"]]}'`,
					});
				});
			}
			if (alignment == "city") {
				parkServePlaceLayer.visible = true;
				if (priorityAreaUrbanFL.visible) {
					priorityAreaPlaceFL.visible = true;
				}
				parkServeUrbanLayer.visible = false;
				priorityAreaUrbanFL.visible = false;
				mapView.goTo(city?.geometry);
				mapView.whenLayerView(priorityAreaUrbanFL).then((layerView) => {
					layerView.filter = new FeatureFilter({
						where: "1 = 1",
					});
				});
			}
		}
	}, [alignment]);

	const handleToggle = (
		event: React.MouseEvent<HTMLElement>,
		newAlignment: string
	) => {
		setAlignment(newAlignment);
	};

	const disableZooming = () => {
		miniMapView.popup.dockEnabled = true;

		// stops propagation of default behavior when an event fires
		const stopEvtPropagation = (event: any) => {
			event.stopPropagation();
		};

		// disable mouse wheel scroll zooming on the view
		miniMapView.on("mouse-wheel", stopEvtPropagation);

		// disable zooming via double-click on the view
		miniMapView.on("double-click", stopEvtPropagation);

		// disable zooming out via double-click + Control on the view
		miniMapView.on("double-click", ["Control"], stopEvtPropagation);

		// disables pinch-zoom and panning on the view
		miniMapView.on("drag", stopEvtPropagation);

		// disable the view's zoom box to prevent the Shift + drag
		// and Shift + Control + drag zoom gestures.
		miniMapView.on("drag", ["Shift"], stopEvtPropagation);
		miniMapView.on("drag", ["Shift", "Control"], stopEvtPropagation);

		// prevents zooming with the + and - keys
		miniMapView.on("key-down", (event) => {
			const prohibitedKeys = [
				"+",
				"-",
				"Shift",
				"_",
				"=",
				"ArrowUp",
				"ArrowDown",
				"ArrowRight",
				"ArrowLeft",
			];
			const keyPressed = event.key;
			if (prohibitedKeys.indexOf(keyPressed) !== -1) {
				event.stopPropagation();
			}
		});
	};

	useEffect(() => {
		if (city || clickedPark || gpJson) {
			//setCityVis(true);
			if (!matches) {
				setVis(true);
			}
		}
	}, [city, clickedPark, gpJson]);

	return (
		<Collapse
			in={visible}
			className={"right-panel__container"}
		>
			{city || clickedPark || gpJson ? (
				<Box
					px={1}
					py={3}
					bgcolor="white"
					boxSizing="border-box"
				>
					<Box
						display="flex"
						flexDirection="row"
						justifyContent="space-between"
						alignItems="center"
						sx={{ marginBottom: "5px" }}
					>
						<ToggleButtonGroup
							fullWidth
							color="primary"
							value={alignment}
							exclusive
							onChange={handleToggle}
							aria-label="Platform"
						>
							<ToggleButton value="city">City</ToggleButton>
							<ToggleButton value="urban">Urban Area</ToggleButton>
							{gpJson && (
								<ToggleButton value="evaluate">Evaluator</ToggleButton>
							)}
							{clickedPark && <ToggleButton value="park">Park</ToggleButton>}
						</ToggleButtonGroup>
					</Box>

					{alignment == "city" && city ? (
						<CityInfoForPanel />
					) : alignment == "park" && clickedPark ? (
						<ClickedParkPanel />
					) : alignment == "evaluate" && gpJson ? (
						<EvaluatorResultsPanel />
					) : (
						<UrbanAreaInfoForPanel urbanarea={urban} />
					)}
				</Box>
			) : (
				<NationalStats />
			)}
		</Collapse>
	);
}
